import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { UsageChart } from '@/components/charts/UsageChart'
import { CostChart } from '@/components/charts/CostChart'
import { TierBreakdown } from '@/components/charts/TierBreakdown'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell, TableWrapper } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { getSupabaseWithClerkAuth } from '@/lib/supabase'
import { formatCurrency, formatTokens } from '@/lib/currency'
import type { DailyUsage, TierUsage } from '@/lib/supabase'

type DateRange = '7d' | '30d' | '90d'

interface ModelUsage {
  model: string
  tier: string
  total_tokens: number
  total_cost_usd: number
  request_count: number
}

const DATE_RANGES: { value: DateRange; label: string; days: number }[] = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '30d', label: '30 days', days: 30 },
  { value: '90d', label: '90 days', days: 90 },
]

export default function UsagePage() {
  const [dateRange, setDateRange] = useState<DateRange>('30d')
  const { getToken } = useAuth()

  const days = DATE_RANGES.find(r => r.value === dateRange)?.days ?? 30

  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db.from('customers').select('currency_preference').single()
      return data
    },
  })
  const currency = customer?.currency_preference || 'PHP'

  const { data: dailyUsage = [] } = useQuery<DailyUsage[]>({
    queryKey: ['daily-usage', days],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const since = new Date()
      since.setDate(since.getDate() - days)
      const { data } = await db
        .from('customer_usage_daily')
        .select('*')
        .gte('day', since.toISOString().split('T')[0])
        .order('day', { ascending: true })
      return data || []
    },
  })

  const { data: tierUsage = [] } = useQuery<TierUsage[]>({
    queryKey: ['tier-usage', days],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const since = new Date()
      since.setDate(since.getDate() - days)
      const { data } = await db
        .from('usage_events')
        .select('tier, total_tokens, cost_usd')
        .gte('created_at', since.toISOString())
      if (!data?.length) return []
      const grouped = data.reduce(
        (acc, row) => {
          if (!acc[row.tier]) acc[row.tier] = { tier: row.tier, total_tokens: 0, total_cost_usd: 0 }
          acc[row.tier].total_tokens += row.total_tokens
          acc[row.tier].total_cost_usd += row.cost_usd
          return acc
        },
        {} as Record<string, TierUsage>
      )
      return Object.values(grouped)
    },
  })

  const { data: modelUsage = [] } = useQuery<ModelUsage[]>({
    queryKey: ['model-usage', days],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const since = new Date()
      since.setDate(since.getDate() - days)
      const { data } = await db
        .from('usage_events')
        .select('model, tier, total_tokens, cost_usd')
        .gte('created_at', since.toISOString())
      if (!data?.length) return []
      const grouped = data.reduce(
        (acc, row) => {
          if (!acc[row.model]) acc[row.model] = { model: row.model, tier: row.tier, total_tokens: 0, total_cost_usd: 0, request_count: 0 }
          acc[row.model].total_tokens += row.total_tokens
          acc[row.model].total_cost_usd += row.cost_usd
          acc[row.model].request_count += 1
          return acc
        },
        {} as Record<string, ModelUsage>
      )
      return Object.values(grouped).sort((a, b) => b.total_tokens - a.total_tokens)
    },
  })

  const totalTokens = dailyUsage.reduce((s, d) => s + d.total_tokens, 0)
  const totalCost = dailyUsage.reduce((s, d) => s + d.total_cost_usd, 0)
  const totalRequests = dailyUsage.reduce((s, d) => s + d.request_count, 0)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header + date range */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Usage Analytics</h2>
          <p className="text-sm text-gray-500 mt-1">
            {formatTokens(totalTokens)} tokens · {totalRequests.toLocaleString()} requests · {formatCurrency(totalCost, currency)} cost
          </p>
        </div>
        <Tabs value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
          <TabsList>
            {DATE_RANGES.map(r => (
              <TabsTrigger key={r.value} value={r.value}>{r.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Token Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <UsageChart data={dailyUsage} height={240} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Cost ({currency})</CardTitle>
          </CardHeader>
          <CardContent>
            <CostChart data={dailyUsage} currency={currency} height={240} />
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Usage by Tier</CardTitle>
          </CardHeader>
          <CardContent>
            <TierBreakdown data={tierUsage} height={260} />
          </CardContent>
        </Card>

        {/* Top models */}
        <Card>
          <CardHeader>
            <CardTitle>Top Models</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Requests</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modelUsage.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-gray-400">
                      No usage yet in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  modelUsage.map(m => (
                    <TableRow key={m.model}>
                      <TableCell>
                        <code className="text-xs font-mono text-gray-700">{m.model}</code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={m.tier as 'speed' | 'standard' | 'premium' | 'media'}>
                          {m.tier}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatTokens(m.total_tokens)}</TableCell>
                      <TableCell>{formatCurrency(m.total_cost_usd, currency)}</TableCell>
                      <TableCell>{m.request_count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
